/**
 * reconcileSuggestions — pure Vorschlags-Orchestrierung für den Abgleich-Tab
 * (BR-6, architecture-bank-reconciliation.md §9.2 „Exclusion-Vertrag").
 *
 * Verbindlicher Aufrufer-Vorfilter (§9.2, Matching-Reviewer F1): Die
 * Match-Engines (`matchIncomingTransactions`/`matchOutgoingTransactions`) tragen
 * BEWUSST KEINEN `excludedTxIds`-Parameter. Die Ausschluss-Pflicht liegt beim
 * Aufrufer: VOR den Engines werden
 *   - interne Umbuchungen (`detectInternalTransfers`, BEIDE Seiten) und
 *   - Rücklastschriften (`detectPaymentReversals`, Debit-Seite)
 * aus der Kandidatenmenge herausgefiltert. Eine Umbuchungs-Gutschrift ist keine
 * Kundenzahlung und eine Rücklastschrift keine Ausgabe — würden sie ungefiltert
 * in die Engine gehen, entstünde ein falscher Rechnungs-/Ausgaben-Vorschlag, den
 * der Compiler nicht fängt (die Engine kennt die Vorstufen nicht). Derselbe
 * Ausschluss speist `selectTransactionsWithoutReceipt` (§8.6).
 *
 * Reine Funktion (keine Firestore-Imports, keine React) → in jest-jsdom testbar
 * (F1-Belegtest „Umbuchungs-Credit erzeugt keinen Invoice-Vorschlag").
 *
 * @module reconcileSuggestions
 */

import { BankAccount, BankTransaction } from '../../types/bankTypes';
import {
  MatchableInvoice,
  MatchableExpense,
  MatchSuggestion,
  InternalTransferPair,
  ReversalSuggestion,
  matchIncomingTransactions,
  matchOutgoingTransactions,
  detectInternalTransfers,
  detectPaymentReversals,
  selectTransactionsWithoutReceipt,
} from '../../utils/bankMatching';

/** Eingabe für {@link buildReconcileSuggestions}. */
export interface ReconcileSuggestionsInput {
  /**
   * Unmatched Umsätze (beide Vorzeichen) — die eigentliche Kandidatenmenge für
   * Rechnungs-/Ausgaben-Vorschläge und die „Ohne Beleg"-Liste.
   */
  unmatchedTxs: BankTransaction[];
  /**
   * ALLE geladenen Umsätze (beide Vorzeichen, ALLE Konten) — nötig für die
   * Spiegel-Suche der Umbuchungs-Erkennung (§8.6 Vorstufe 1), deren Gegenseite
   * bereits `matched`/anderes Konto sein kann.
   */
  allTxs: BankTransaction[];
  /**
   * Bereits `matched` Gutschriften mit `paymentIds.length ≥ 1` — Grundlage der
   * Rücklastschrift-Erkennung (§8.9): die frühere, verbuchte Gutschrift.
   */
  matchedCreditTxs: BankTransaction[];
  invoices: MatchableInvoice[];
  expenses: MatchableExpense[];
  accounts: BankAccount[];
  /** Heutiges Datum `YYYY-MM-DD` (Reinheit — Muster `openItemStatus.ts`). */
  todayISO: string;
}

/** Ergebnis von {@link buildReconcileSuggestions}. */
export interface ReconcileSuggestionsResult {
  /** Eingangs-Vorschläge (Rechnungen) NACH dem Vorfilter. */
  incoming: MatchSuggestion[];
  /** Ausgangs-Vorschläge (Ausgaben) NACH dem Vorfilter. */
  outgoing: MatchSuggestion[];
  /** Erkannte interne Umbuchungs-Paare (eigener Bulk-Flow). */
  internalTransfers: InternalTransferPair[];
  /** Erkannte Rücklastschriften (eigener Storno-Flow, §8.9). */
  reversals: ReversalSuggestion[];
  /** Unmatched Debits OHNE Beleg (exkl. Umbuchungen/Reversals), §8.6. */
  withoutReceipt: BankTransaction[];
  /** Vereinigte Ausschluss-Menge (Umbuchung beidseitig + Reversal-Debit). */
  excludedTxIds: Set<string>;
}

/**
 * Berechnet alle Abgleich-Vorschläge mit dem verbindlichen Aufrufer-Vorfilter
 * (§9.2). Reihenfolge:
 *   1. Vorstufen erkennen (Umbuchung beidseitig, Rücklastschrift Debit-Seite),
 *   2. deren txIds zur Ausschluss-Menge vereinen,
 *   3. bereinigte Kandidatenliste in beide Match-Engines geben,
 *   4. „Ohne Beleg" aus unmatched Debits minus Ausschluss.
 *
 * @param input - siehe {@link ReconcileSuggestionsInput}.
 * @returns {@link ReconcileSuggestionsResult}.
 */
export function buildReconcileSuggestions(
  input: ReconcileSuggestionsInput,
): ReconcileSuggestionsResult {
  const {
    unmatchedTxs,
    allTxs,
    matchedCreditTxs,
    invoices,
    expenses,
    accounts,
    todayISO,
  } = input;

  // 1. Vorstufen (§8.6 Vorstufe 1 + §8.9).
  const internalTransfers = detectInternalTransfers(allTxs, accounts);
  const unmatchedDebits = unmatchedTxs.filter((tx) => tx.amountCents < 0);
  const reversals = detectPaymentReversals(unmatchedDebits, matchedCreditTxs);

  // 2. Ausschluss-Menge: Umbuchung BEIDE Seiten + Reversal-Debit.
  const excludedTxIds = new Set<string>();
  for (const pair of internalTransfers) {
    excludedTxIds.add(pair.debitTxId);
    if (pair.creditTxId) {
      excludedTxIds.add(pair.creditTxId);
    }
  }
  for (const rev of reversals) {
    excludedTxIds.add(rev.debitTxId);
  }

  // 3. Bereinigte Kandidatenliste in die Engines (die self-filtern nach
  //    Vorzeichen — Eingang >0, Ausgang <0).
  const candidateTxs = unmatchedTxs.filter((tx) => !excludedTxIds.has(tx.id));
  const ownIbans = accounts
    .map((a) => a.iban)
    .filter((iban): iban is string => iban != null && iban.length > 0);

  const incoming = matchIncomingTransactions(candidateTxs, invoices, todayISO);
  const outgoing = matchOutgoingTransactions(candidateTxs, expenses, ownIbans, todayISO);

  // 4. „Ohne Beleg" (§8.6): unmatched Debits minus Ausschluss.
  const withoutReceipt = selectTransactionsWithoutReceipt(unmatchedDebits, excludedTxIds);

  return {
    incoming,
    outgoing,
    internalTransfers,
    reversals,
    withoutReceipt,
    excludedTxIds,
  };
}
